// src/components/post/PostActions.jsx
import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaHeart, FaRegHeart, FaComment, FaShare, FaBookmark, FaRegBookmark } from 'react-icons/fa';
import { UserContext } from '../../context/userContext';

const PostActions = ({ post, onUpdate, showComments = false, toggleComments }) => {
  const { currentUser } = useContext(UserContext);
  const [isLiked, setIsLiked] = useState(post?.liked || false);
  const [likeCount, setLikeCount] = useState(post?.likes || 0);
  const [isSaving, setIsSaving] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  
  const navigate = useNavigate();

  // Handle like/unlike
  const toggleLike = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/likes`,
        {
          targetId: post._id,
          targetType: 'post',
          reaction: 'like'
        },
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );

      const { liked } = response.data;
      setIsLiked(liked);
      setLikeCount(prev => liked ? prev + 1 : prev - 1);
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  // Handle share
  const handleShare = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    setIsSaving(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/posts/${post._id}/share`,
        {},
        {
          headers: { Authorization: `Bearer ${currentUser.token}` }
        }
      );

      // Show success message or update UI
      alert('Post shared successfully!');
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error sharing post:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle bookmark
  const toggleBookmark = () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    setIsBookmarked(prev => !prev);
    // Here you would also make an API call to save the bookmark status
  };

  return (
    <div className="flex items-center justify-between border-t border-b border-gray-200 py-3 px-1 my-4">
      <div className="flex items-center gap-6">
        {/* Like button */}
        <button 
          onClick={toggleLike}
          className="flex items-center gap-1 text-gray-600 hover:text-red transition-colors"
          aria-label={isLiked ? "Unlike" : "Like"}
        >
          {isLiked ? (
            <FaHeart className="text-red text-xl" />
          ) : (
            <FaRegHeart className="text-xl" />
          )}
          <span className="text-sm font-medium">
            {likeCount > 0 ? likeCount : ''}
          </span>
        </button>
        
        {/* Comment button */}
        <button 
          onClick={toggleComments}
          className={`flex items-center gap-1 ${showComments ? 'text-primary' : 'text-gray-600 hover:text-primary'} transition-colors`}
          aria-label="Comment"
        >
          <FaComment className="text-xl" />
          <span className="text-sm font-medium">
            {post?.comments > 0 ? post.comments : ''}
          </span>
        </button>
        
        {/* Share button */}
        <button 
          onClick={handleShare}
          disabled={isSaving}
          className="flex items-center gap-1 text-gray-600 hover:text-primary transition-colors disabled:opacity-50"
          aria-label="Share"
        >
          <FaShare className="text-xl" />
          <span className="text-sm font-medium">
            {post?.shares > 0 ? post.shares : ''}
          </span>
        </button>
      </div>
      
      {/* Bookmark button */}
      <button 
        onClick={toggleBookmark}
        className="text-gray-600 hover:text-primary transition-colors"
        aria-label={isBookmarked ? "Remove bookmark" : "Bookmark"}
      >
        {isBookmarked ? (
          <FaBookmark className="text-primary text-xl" />
        ) : (
          <FaRegBookmark className="text-xl" />
        )}
      </button>
    </div>
  );
};

export default PostActions;